import type React from "react";

/**
 * busyness.ts — per-element heartbeat timing driven by real busyness.
 *
 * The `--beat-dur` CSS variable controls both the `.heart-mark` beat animation
 * and the `.live-ring::after` ring animation, so setting it once on the
 * element synchronises both channels without JS timers.
 *
 * Mapping (DESIGN.md → motion; the pulse rate encodes busyness): busier ⇒
 * shorter period (faster beat), clamped so the UI never strobes. Formula:
 *
 *   --beat-dur = clamp(0.95s, 1.9s − b·1.0s, 1.9s)
 *
 * where b ∈ [0, 1] is a continuous normalised busyness derived from the
 * actual `totalWaiting` count relative to a reference ceiling (not from the
 * coarse 4-state level), so two equally-"moderate" hospitals can still beat
 * at slightly different rates.
 *
 * Regional hero uses the same formula on the regional total.
 */

// ── Constants ──────────────────────────────────────────────────────────────────

/**
 * Reference ceiling for normalising per-hospital waiting counts.
 * Derived from historical 95th-percentile (~20 people waiting at a
 * single busy South-Tyrol ER). Values above this saturate to b=1.
 */
const HOSPITAL_SATURATION = 20;

/**
 * Reference ceiling for the regional total (7 hospitals × 20).
 */
const REGIONAL_SATURATION = HOSPITAL_SATURATION * 7; // 140

// ── Core formula ───────────────────────────────────────────────────────────────

/**
 * Map a continuous busyness value b ∈ [0, 1] to an animation duration.
 * Clamped to [0.95, 1.9] so the mark never strobes (hard floor).
 */
function busynessToDuration(b: number): number {
  // Clamp b to [0, 1]
  const bc = Math.max(0, Math.min(1, b));
  return Math.max(0.95, Math.min(1.9, 1.9 - bc * 1.0));
}

// ── Per-hospital beat duration ──────────────────────────────────────────────────

/**
 * Returns the CSS animation duration (in seconds) for the heartbeat animation,
 * using a continuous mapping from `totalWaiting`.
 *
 * The legacy `level` param is no longer used for the period calculation —
 * the raw count gives finer resolution so two equally-"moderate" hospitals
 * beat at slightly different rates. Unknown (hasData=false) falls back to a
 * calm, non-alarming 1.7s.
 *
 * @param totalWaiting - current people-waiting count for this hospital
 * @param level - congestion level; used only to detect unknown/no-data state
 */
export function beatDuration(totalWaiting: number, level: string): number {
  if (level === "unknown") return 1.7;
  const b = totalWaiting / HOSPITAL_SATURATION;
  return busynessToDuration(b);
}

// ── Regional beat duration ──────────────────────────────────────────────────────

/**
 * Regional beat duration: uses the same continuous formula as per-hospital.
 * Called by the hero with the Σ totalWaiting across all hospitals.
 *
 * Returns 1.7 for zero (no data / all hospitals offline).
 */
export function regionalBeatDuration(total: number): number {
  if (total === 0) return 1.7;
  const b = total / REGIONAL_SATURATION;
  return busynessToDuration(b);
}

// ── Style helper ───────────────────────────────────────────────────────────────

/**
 * Returns a React inline style object that sets `--beat-dur` for a given
 * duration. Both `.heart-mark` beat and `.live-ring::after` ring animations
 * read this var.
 */
export function beatDurationStyle(dur: number): React.CSSProperties {
  return { "--beat-dur": `${dur}s` } as React.CSSProperties;
}

/**
 * Returns a tiny per-card animation-delay (0–120ms) seeded from the hospital
 * slug or any string identifier, so cards with identical busyness don't lock-
 * step on first paint (phasing).
 */
export function cardAnimDelay(seed: string): number {
  // Deterministic hash → delay in [0, 120] ms, expressed in seconds
  let h = 0;
  for (let i = 0; i < seed.length; i++) {
    h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  }
  return (h % 121) / 1000; // 0–0.120 s
}

/**
 * Returns a React inline style object that sets `--beat-delay` — a small
 * NEGATIVE animation-delay (0 to -120ms) seeded from the hospital slug. A
 * negative delay starts the beat already in progress (no initial pause), so
 * cards with identical busyness (hence identical `--beat-dur`) still beat
 * out of phase rather than locking step (phasing). The `.heart-mark`
 * and `.live-ring::after` animations read this var; it cascades from the
 * card root.
 */
export function beatPhaseStyle(seed: string): React.CSSProperties {
  return { "--beat-delay": `-${cardAnimDelay(seed)}s` } as React.CSSProperties;
}
